import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, type AuthUser } from '@/auth/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LocaleSwitcher } from '@/components/locale-switcher';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { BrandLogo } from '@/components/brand-logo';
import { useT } from '@/i18n/use-t';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const t = useT('admin_ui');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setSubmitError(null);
    try {
      const res = await apiClient.post<{ user: AuthUser; accessToken: string }>(
        '/auth/login',
        values,
      );
      // The login payload carries isSuperAdmin + effective permission
      // codes (RFL-106); AuthUser persists them for defensive UI gating.
      login(res.data.user, res.data.accessToken);
      navigate('/', { replace: true });
    } catch {
      setSubmitError(t('login.invalidCredentials', 'Invalid credentials'));
    }
  }

  return (
    <div className="reform-surface-canvas flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6 rounded-card border border-border bg-card p-8">
        <div className="flex items-start justify-between gap-3">
          <BrandLogo height={42} />
          <div className="flex items-center gap-2">
            <ThemeSwitcher />
            <LocaleSwitcher />
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-2">
            <Label htmlFor="email">{t('login.email', 'Email')}</Label>
            <Input id="email" type="email" autoComplete="username" {...register('email')} />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('login.password', 'Password')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            )}
          </div>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? '…' : t('login.submit', 'Sign in')}
          </Button>
        </form>
      </div>
    </div>
  );
}
